import React, { useEffect, useRef, useState } from 'react'

import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl'

import {
  DropdownLabel,
  DropdownMenu,
  DropdownValue,
  DropdownWrapper,
} from './FilterDropdown.styled'
import { IFilterOption } from './filterOptions'

interface ISingleProps<TValue extends string> {
  mode: 'single'
  labelKey: string
  options: IFilterOption<TValue>[]
  value: TValue | undefined
  onChange: (next: TValue | undefined) => void
  testId?: string
}

interface IMultiProps<TValue extends string> {
  mode: 'multi'
  labelKey: string
  options: IFilterOption<TValue>[]
  value: TValue[] | undefined
  onChange: (next: TValue[] | undefined) => void
  testId?: string
}

type IProps<TValue extends string> = (ISingleProps<TValue> | IMultiProps<TValue>) &
  WrappedComponentProps

function FilterDropdownInner<TValue extends string>(props: IProps<TValue>): JSX.Element {
  const { intl, labelKey, options, testId } = props
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const handler = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const valueDisplay = (() => {
    if (props.mode === 'single') {
      if (!props.value) return intl.formatMessage({ id: 'creatorSearch.filter.unset' })
      const opt = options.find((o) => o.value === props.value)
      return opt ? intl.formatMessage({ id: opt.labelKey }) : props.value
    }
    if (!props.value || props.value.length === 0) {
      return intl.formatMessage({ id: 'creatorSearch.filter.unset' })
    }
    return props.value
      .map((v) => {
        const opt = options.find((o) => o.value === v)
        return opt ? intl.formatMessage({ id: opt.labelKey }) : v
      })
      .join(', ')
  })()

  const handleSelect = (value: TValue): void => {
    if (props.mode === 'single') {
      props.onChange(props.value === value ? undefined : value)
      setOpen(false)
      return
    }
    const current = props.value ?? []
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    props.onChange(next.length === 0 ? undefined : next)
  }

  const isAnySelected =
    props.mode === 'single' ? props.value === undefined : !props.value || props.value.length === 0

  const handleSelectAny = (e: React.MouseEvent): void => {
    e.stopPropagation()
    props.onChange(undefined as any)
    setOpen(false)
  }

  return (
    <DropdownWrapper
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      onClick={() => setOpen((v) => !v)}
      data-testid={testId}
    >
      <DropdownLabel>
        <FormattedMessage id={labelKey} />
      </DropdownLabel>
      <DropdownValue>{valueDisplay}</DropdownValue>
      {open && (
        <DropdownMenu role="menu">
          <li role="menuitem" data-selected={isAnySelected} data-any-item onClick={handleSelectAny}>
            <span>
              <FormattedMessage id="creatorSearch.filter.unset" />
            </span>
          </li>
          {options.map((opt) => {
            const isSelected =
              props.mode === 'single'
                ? props.value === opt.value
                : (props.value ?? []).includes(opt.value)
            return (
              <li
                key={opt.value}
                role="menuitem"
                data-selected={isSelected}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSelect(opt.value)
                }}
              >
                {props.mode === 'multi' && <input type="checkbox" checked={isSelected} readOnly />}
                <span>
                  <FormattedMessage id={opt.labelKey} />
                </span>
              </li>
            )
          })}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  )
}

const FilterDropdown = (injectIntl(FilterDropdownInner) as unknown) as <TValue extends string>(
  props: ISingleProps<TValue> | IMultiProps<TValue>,
) => JSX.Element

export { FilterDropdown }
export default FilterDropdown
